import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import { useParams } from "react-router-dom";
import VendorEditForm from "./VendorEditForm";
import { getVendorsByContact } from "../api";
import { Vendor, Contact } from "../types";

const VendorEditPage: React.FC = () => {
  const { id } = useParams();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | undefined>();
  const [contact, setContact] = useState<Contact | undefined>();

  const [viewState, setViewState] = useState<
    "loading" | "overview" | "details" | "error"
  >("loading");

  useEffect(() => {
    setViewState("loading");
    if (!id) return;
    getVendorsByContact(id)
      .then((res) => {
        setVendors(res);
        setContact(res[0].contact);
        setViewState("overview");
      })
      .catch(() => {
        setViewState("error");
      });
  }, [id]);

  // Sort vendors: without mainVendor first
  const sortedVendors = [...vendors].sort((a, b) => {
    if (!a.mainVendor && b.mainVendor) return -1;
    if (a.mainVendor && !b.mainVendor) return 1;
    return 0;
  });

  const formatPaymentDate = (vendors: Vendor[]) => {
    const date = vendors.find((v) => !v.mainVendor)?.paymentDate;
    if (!date) return "N/A";

    let d: Date;

    if (typeof date === "string") {
      d = new Date(date);
    } else if (date instanceof Date) {
      d = new Date(date); // lav en kopi for sikkerhed
    } else {
      return "N/A";
    }

    // Tilføj 1 år
    d.setFullYear(d.getFullYear() + 1);

    // Formater som DD/MM/YYYY
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0"); // måneder er 0-baserede
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  return (
    <>
      {viewState === "loading" && (
        <Typography sx={{ color: "#715939", mt: 4 }}>Loading...</Typography>
      )}

      {viewState === "overview" && (
        <Container
          disableGutters
          maxWidth={false}
          sx={{
            minHeight: "100vh",
            p: { xs: 2, md: 4 },
            bgcolor: "#faead7", // antique-white
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: "#715939", fontWeight: 600, mb: 3 }}
          >
            Overblik
          </Typography>

          <Grid
            container
            spacing={2}
            direction="column"
            alignItems="flex-start"
          >
            {sortedVendors.map((vendor) => (
              <Grid item key={vendor.id} sx={{ width: "100%" }}>
                <Card
                  sx={{
                    bgcolor: "#fffaf5",
                    borderRadius: 3,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    maxWidth: 500,
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setViewState("details");
                    }}
                  >
                    <CardContent sx={{ textAlign: "left" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#715939", fontWeight: 600 }}
                      >
                        {vendor.name} - {vendor.type?.name}
                      </Typography>
                      <Typography sx={{ color: "#2b2218", mt: 1 }}>
                        {vendor.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#8b6d46", fontWeight: 500, mt: 1 }}
                      >
                        Klik for detaljer →
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}

            {/* Kontaktperson kort nederst */}
            {contact && (
              <Grid item sx={{ width: "auto", mt: 2 }}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#fffaf5",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    maxWidth: 400,
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#715939", fontWeight: 500 }}
                  >
                    Interne kontaktoplysninger
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ color: "#2b2218", textAlign: "left" }}>
                    <Typography>
                      <strong>Navn:</strong> {contact.name}
                    </Typography>
                    <Typography>
                      <strong>Telefon:</strong> {contact.phone}
                    </Typography>
                    <Typography>
                      <strong>Email:</strong> {contact.email}
                    </Typography>
                    <Typography>
                      <strong>Udløbsdato:</strong> {formatPaymentDate(vendors)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      )}

      {viewState === "details" && selectedVendor && (
        <VendorEditForm
          vendors={vendors}
          updatedVendors={(_vendors) => setVendors(_vendors)}
          initialVendor={selectedVendor}
          returnCallback={() => setViewState("overview")}
        />
      )}

      {viewState === "error" && (
        <Container maxWidth="sm">
          <Typography variant="h6" color="error" mt={4}>
            Vendor not found.
          </Typography>
        </Container>
      )}
    </>
  );
};

export default VendorEditPage;
